import * as signalR from "@microsoft/signalr";
import { HUB_URL } from "../../constants/urls";
import { ENV } from "../../constants/environment";

class HubConnector {
    connection = null;
    retryCount = 0;
    maxRetries = 3;
    retryDelay = 2000; // 2 seconds
    isInitialized = false;

    constructor() {
        // Constructor artık sadece instance oluşturuyor, bağlantı kurmuyor
    }

    initializeConnection() {
        if (this.isInitialized) {
            return;
        }

        // Get authentication token
        const token = this.getAuthToken();

        if (!token) {
            return;
        }

        const hubUrl = `${ENV.apiUrl}${HUB_URL}`;

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => {
                    return token;
                }
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        // Add connection event handlers
        this.connection.onclose(() => {
            this.isInitialized = false; // Allow re-initialization
        });

        this.connection.onreconnecting(() => {
            // Silent reconnect
        });

        this.connection.onreconnected(() => {
            this.retryCount = 0; // Reset retry count on successful reconnection
        });

        // Note: onstatechange is not available in SignalR 7.x
        // State changes are handled by onclose, onreconnecting, and onreconnected events above

        this.isInitialized = true;
    }

    getAuthToken() {
        try {
            // Try different token storage keys
            let token = localStorage.getItem('LOGIN_DR');
            if (!token) {
                token = localStorage.getItem('token');
            }
            if (!token) {
                token = localStorage.getItem('authToken');
            }
            if (!token) {
                token = localStorage.getItem('accessToken');
            }

            // Check token validity
            if (token) {
                // Try to decode JWT token to see if it contains user ID
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));

                    // Check if token is expired
                    const currentTime = Math.floor(Date.now() / 1000);
                    if (payload.exp && payload.exp < currentTime) {
                        // Try to refresh token
                        return this.refreshToken();
                    }

                    // Look for user ID in common claim names
                    const userId = payload.sub || payload.nameid || payload.userId || payload.id || payload.name;

                    if (!userId) {
                        return null;
                    }
                } catch (decodeError) {
                    return null;
                }
            }

            return token;
        } catch (error) {
            console.error('❌ [SignalR] Token alma hatası:', error);
            return null;
        }
    }

    async refreshToken() {
        try {
            // Try to refresh token via API
            const response = await fetch(`${ENV.apiUrl}/api/auth/refreshToken`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (data.token) {
                    // Store new token
                    localStorage.setItem('token', data.token);
                    return data.token;
                }
            }

            return null;
        } catch (error) {
            return null;
        }
    }

    async startConnection() {
        try {
            // Check if we have a valid token before attempting connection
            const token = this.getAuthToken();
            if (!token) {
                return false;
            }

            // Initialize connection if not already done
            if (!this.isInitialized) {
                this.initializeConnection();
            }

            if (!this.connection) {
                return false;
            }

            if (this.connection.state === signalR.HubConnectionState.Disconnected) {
                // Start connection with timeout
                const connectionPromise = this.connection.start();
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error('Connection timeout')), 10000); // 10 second timeout
                });

                await Promise.race([connectionPromise, timeoutPromise]);

                // Wait for connection ID to be available
                if (!this.connection.connectionId) {
                    let attempts = 0;
                    while (!this.connection.connectionId && attempts < 100) { // Max 10 seconds
                        await new Promise(resolve => setTimeout(resolve, 100));
                        attempts++;
                    }

                    if (!this.connection.connectionId) {
                        return false;
                    }
                }

                this.retryCount = 0; // Reset retry count on successful connection
                return true;
            } else {
                return this.connection.state === signalR.HubConnectionState.Connected;
            }
        } catch (err) {
            this.retryCount++;

            if (this.retryCount < this.maxRetries) {
                const delay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 10000);
                setTimeout(() => {
                    this.startConnection();
                }, delay);
            }
            return false;
        }
    }

    stopConnection() {
        if (this.connection) {
            this.connection.stop();
        }
    }

    getConnection() {
        // Initialize connection if not already done
        if (!this.isInitialized) {
            this.initializeConnection();
        }
        return this.connection;
    }

    isConnected() {
        // Initialize connection if not already done
        if (!this.isInitialized) {
            this.initializeConnection();
        }
        return this.connection && this.connection.state === signalR.HubConnectionState.Connected;
    }

    getConnectionState() {
        // Initialize connection if not already done
        if (!this.isInitialized) {
            this.initializeConnection();
        }
        return this.connection ? this.connection.state : 'No connection';
    }
}

const connector = new HubConnector();

export default connector;